import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw, AlertTriangle, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class GarcomErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    if (typeof document !== 'undefined') {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('❌ [GARCOM-ERROR-BOUNDARY] Erro capturado:', error, errorInfo);
    if (typeof document !== 'undefined') {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    this.setState({ errorInfo });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/garcom/home';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
          <div className="bg-gray-800/90 border border-gray-700/60 rounded-3xl p-6 max-w-md w-full text-center shadow-2xl backdrop-blur-sm">
            <div className="w-16 h-16 bg-red-500/20 text-red-400 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/30">
              <AlertTriangle className="h-8 w-8" />
            </div>

            <h2 className="text-xl font-bold text-white mb-2">
              Algo não saiu como esperado
            </h2>

            <p className="text-gray-400 text-sm mb-6">
              Ocorreu um erro temporário na exibição do sistema do garçom. Você pode tentar recarregar a tela ou voltar ao início.
            </p>

            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <div className="bg-black/50 p-3 rounded-xl text-left mb-6 overflow-x-auto max-h-32">
                <p className="text-red-400 font-mono text-xs font-semibold">{this.state.error.toString()}</p>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button
                onClick={this.handleReset}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 hover:from-orange-600 hover:to-red-600 transition-all active:scale-95 shadow-lg shadow-orange-500/20"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Recarregar Sistema</span>
              </button>

              <button
                onClick={this.handleGoHome}
                className="w-full bg-gray-700/80 hover:bg-gray-600 text-gray-200 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                <Home className="h-4 w-4" />
                <span>Voltar ao Início do Garçom</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GarcomErrorBoundary;
